import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboard, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  CurrencyEuroIcon,
  TicketIcon,
  UsersIcon,
  TruckIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const { data, isLoading, error } = useDashboard();
  const { invalidateDashboard } = useInvalidate();

  const handleCheckin = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/checkin`);
      toast.success('Check-in u krye!');
      invalidateDashboard();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">Gabim gjatë ngarkimit të dashboard</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Stats Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CurrencyEuroIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Shitjet Sot</p>
              <p className="text-2xl font-bold text-gray-900">€{data?.today?.sales?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Fitimi Muaji (Neto)</p>
              <p className="text-2xl font-bold text-gray-900">€{data?.month?.net_profit?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Borxhe Klientësh</p>
              <p className="text-2xl font-bold text-red-600">€{data?.debts?.clients?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Balanca Bankare</p>
              <p className="text-2xl font-bold text-green-600">€{data?.bank_balance_eur?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-r from-green-500 to-green-600 text-white">
          <p className="text-green-100">Shitje Muaji</p>
          <p className="text-3xl font-bold">€{data?.month?.sales?.toFixed(2)}</p>
          <p className="text-green-100 text-sm mt-1">{data?.month?.tickets_count} bileta</p>
        </div>

        <div className="card bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <p className="text-blue-100">Fitim Bruto (TSC)</p>
          <p className="text-3xl font-bold">€{data?.month?.profit?.toFixed(2)}</p>
        </div>

        <div className="card bg-gradient-to-r from-red-500 to-red-600 text-white">
          <p className="text-red-100">Shpenzime Muaji</p>
          <p className="text-3xl font-bold">€{data?.month?.expenses?.toFixed(2)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Check-in Tomorrow */}
        {data?.checkin_tomorrow?.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">🔔 Check-in Nesër</h2>
            <div className="space-y-3">
              {data.checkin_tomorrow.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="font-medium">{ticket.passenger_name}</p>
                    <p className="text-sm text-gray-500">{ticket.ticket_code} - {ticket.destination?.name}</p>
                  </div>
                  <button
                    onClick={() => handleCheckin(ticket.id)}
                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                  >
                    <CheckCircleIcon className="w-6 h-6" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Debtors */}
        {data?.top_debtors?.length > 0 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">⚠️ Borxhlinjt më të Mëdhenj</h2>
              <Link to="/client-debts" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                Shiko të gjithë →
              </Link>
            </div>
            <div className="space-y-3">
              {data.top_debtors.map((client) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <Link to={`/clients/${client.id}`} className="font-medium hover:text-primary-600">
                      {client.name}
                    </Link>
                    <p className="text-sm text-gray-500">{client.code}</p>
                  </div>
                  <p className="font-bold text-red-600">€{client.balance?.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Tickets */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Biletat e Fundit</h2>
            <Link to="/tickets" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Shiko të gjitha →
            </Link>
          </div>
          <div className="space-y-3">
            {data?.recent_tickets?.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{ticket.passenger_name}</p>
                  <p className="text-sm text-gray-500">
                    {ticket.destination?.name} • {dayjs(ticket.ticket_date).format('DD/MM/YYYY')}
                  </p>
                </div>
                <p className="font-semibold text-gray-900">€{ticket.total}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Notifications */}
        {data?.notifications?.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">📢 Njoftime</h2>
            <div className="space-y-3">
              {data.notifications.map((notif) => (
                <div key={notif.id} className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-gray-700">{notif.content}</p>
                  <p className="text-xs text-gray-400 mt-1">{dayjs(notif.created_at).format('DD/MM/YYYY HH:mm')}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
